import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SOLUTIONS } from "../data/solutions";
import Reveal from "./Reveal";

// Awwwards-style numbered capability index (01–07) on the light enterprise canvas.
// Hovering a row reveals a cinematic image preview in the sticky right rail.
export default function CapabilityIndex() {
  const [active, setActive] = useState(0);
  const current = SOLUTIONS[active];

  return (
    <section id="pillars" className="relative bg-white py-24 md:py-32 scroll-mt-20 overflow-hidden" data-testid="capability-index">
      {/* Giant ghost numeral of the active practice */}
      <div className="pointer-events-none absolute right-[-4%] top-1/2 -translate-y-1/2 hidden xl:block" aria-hidden="true">
        <AnimatePresence mode="wait">
          <motion.span
            key={current.num}
            className="giant-num font-display block"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            {current.num}
          </motion.span>
        </AnimatePresence>
      </div>
      <div className="mx-auto max-w-[1320px] px-5 md:px-10 relative">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p className="text-xs font-tech tracking-[0.35em] text-cydeep mb-5 flex items-center gap-3"><span className="h-px w-10 bg-cydeep/50 inline-block" />WHAT WE INTEGRATE</p>
              <h2 className="font-display font-black text-4xl md:text-5xl text-[#101828] leading-[1.05] tracking-tight max-w-xl">
                Seven practices. One accountable partner.
              </h2>
            </div>
            <p className="text-sm text-mist/60 max-w-sm leading-relaxed">
              Twelve technology pillars from the company profile, organised into seven integrated solution practices. Hover to preview, click to explore.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14 items-start">
          {/* Index rows */}
          <div className="border-t border-[#E2E7EF]">
            {SOLUTIONS.map((s, i) => (
              <Reveal key={s.slug} delay={Math.min(i * 0.04, 0.24)}>
                <Link
                  to={`/solutions/${s.slug}`}
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  className={`group grid grid-cols-[44px_1fr_56px] md:grid-cols-[64px_1fr_72px] lg:grid-cols-[64px_1fr_36px] items-center gap-4 md:gap-6 border-b border-[#E2E7EF] py-6 md:py-7 px-3 md:px-5 transition-colors duration-300 ${active === i ? "bg-[#F6F8FA]" : "hover:bg-[#F6F8FA]/70"}`}
                  data-testid={`capability-row-${s.slug}`}
                  data-cursor-label="VIEW"
                >
                  <span className="font-tech text-sm text-ember tabular-nums">{s.num}</span>
                  <div className="min-w-0">
                    <h3 className={`font-display font-bold text-lg md:text-2xl leading-snug transition-all duration-300 ${active === i ? "text-cydeep md:translate-x-1.5" : "text-[#101828]"}`}>
                      {s.shortTitle}
                    </h3>
                    {/* Tagline: always visible on mobile, revealed on hover on desktop */}
                    <p className={`text-[13px] leading-relaxed text-mist/60 overflow-hidden transition-all duration-500 max-h-24 mt-2 lg:mt-0 ${active === i ? "lg:max-h-20 lg:mt-2" : "lg:max-h-0 lg:opacity-0 opacity-70"}`}>
                      {s.realTagline}
                    </p>
                  </div>
                  <img
                    src={`/images/solutions/${s.slug}.jpg`}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    className="lg:hidden h-14 w-full max-w-[88px] object-cover justify-self-end border border-[#E2E7EF]"
                  />
                  <ArrowUpRight
                    size={18}
                    className={`hidden lg:block justify-self-end transition-all duration-300 ${active === i ? "text-ember translate-x-0.5 -translate-y-0.5" : "text-mist/30"}`}
                  />
                </Link>
              </Reveal>
            ))}
          </div>

          {/* Sticky hover-reveal preview */}
          <div className="hidden lg:block sticky top-24" data-testid="capability-preview">
            <div className="relative border border-[#E2E7EF] bg-white p-2.5 shadow-[0_24px_60px_-30px_rgba(10,31,68,0.25)]">
              <div className="relative aspect-[4/3] overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={current.slug}
                    src={`/images/solutions/${current.slug}.jpg`}
                    alt={`${current.title} — solution practice preview`}
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </AnimatePresence>
                <div className="absolute inset-0 bg-gradient-to-t from-[#14151A]/55 via-transparent to-transparent pointer-events-none" />
                <span className="absolute bottom-4 left-4 font-tech text-[10px] tracking-[0.25em] text-white/90">
                  {current.num} // {current.shortTitle.toUpperCase()}
                </span>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {current.services.slice(0, 6).map((sv) => (
                <span key={sv} className="text-[10px] font-tech border border-[#E2E7EF] text-mist/65 px-2.5 py-1 bg-white">
                  {sv}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
